import { GrowthBook } from '@growthbook/growthbook';
import {
  CreateGalleryFromScratchDto,
  GalleryFeedbackOptionsEnum,
  GalleryStatusEnum,
  PriceDtoV2,
  TagColorEnumDto,
} from '@nusign/picflow-backend-types';
import { StripeElementLocale, StripeElementsOptionsModeBase, StripePaymentElementOptions } from '@stripe/stripe-js';
import { getBytesFromGigabytes, getBytesFromMegabytes } from './billing-consts';

export const assetItemSpacing = 10;
export const assetItemSpacingMobile = 8;
export const verticalAssetsItemSpacing = 20;

export const assetFetchCount = 50;
export const assetPaginationThreshold = 100;

export const baseZoom = 1;

export const storyblokImageSizes = [2400, 1920, 1280, 960, 640, 0];
export const storyblokLowQualityMap = {
  2400: 85,
  1920: 85,
  1280: 85,
  960: 85,
  640: 75,
};

export const assetFilenameSpace = 30;

export const multiPartUploadMinSize = 1024 * 1024 * 10;

export const defaultReceiptEmail = 'receipts@picflow.com';

export const multiPartUploadConcurrency = 1;
export const estimatedTimeToCreateAsset = 250;

export const assetSizes = [480, 640, 960, 1280, 1920, 2560, 3840];
export const sizesToAlwaysIncludeMetadataFor = [1920];
export const preGenerateOnUploadSizes = []; // Temporarily disabled until we figure out which sizes to use

export const assetSizesBigToSmall = [...assetSizes].reverse();

export const defaultGalleryIncludes = 'include[]=is_favourite&include[]=unseen_activities';

export const getDefaultGalleryIncludes = (growthBook: GrowthBook) => {
  const includes = [];
  if (!growthBook.isOn('no_gallery_favourites')) {
    includes.push('is_favourite');
  }
  if (!growthBook.isOn('no_gallery_unseen_activities')) {
    includes.push('unseen_activities');
  }
  return includes.length > 0 ? `&include[]=${includes.join('&include[]=')}` : '';
};

// These sizes need to match the sizes in the backend, otherwise archives will fail
export const GALLERY_DOWNLOAD_SIZE = {
  original: 0,
  large: 3840,
  medium: 1920,
  small: 960,
};

export const galleryDownloadSizePreferenceList = ['medium', 'small', 'large', 'original'];

export const downloadSizes: { name: string; size?: number }[] = [
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'small',
    size: GALLERY_DOWNLOAD_SIZE.small,
  },
  {
    name: 'medium',
    size: GALLERY_DOWNLOAD_SIZE.medium,
  },
  {
    name: 'large',
    size: GALLERY_DOWNLOAD_SIZE.large,
  },
  {
    name: 'original',
  },
];

type LanguageKey = 'en' | 'de' | 'fr' | 'es' | 'pt' | 'it';

/** When adding a new language, run yarn i18n:convert to make sure the 18n files exist. They will be in English by default. */
interface LanguageConfig {
  /** You need to add the language code to the LanguageKey type */
  lang: LanguageKey;
  i18n: {
    /** when enabled, the lang will be considered in urls, and the app load the i18n files on the website and auth pages. */
    website: boolean;
    /** when enabled, the app will load the i18n files on the app pages. */
    app: boolean;
    /** when enabled, the app will load the i18n files on tenant domain pages (tenant profile, public galleries and public folders). */
    tenantDomain: boolean;
  };
  programaticPages: {
    /** when enabled, the app will generate the convert pages in that language. Make sure the templates and formats are published in storyblok */
    convert: boolean;
    /** when enabled, the app will generate the compare pages in that language. Make sure the templates and competitors are published in storyblok. */
    compare: boolean;
  };
  /** when enabled, the app will start saving the language to users profiles and it will become selectable in the language switcher on the profile settings page (with a warning, that goes away when i18n.app is true) */
  allowAsUserLanguage: boolean;
}

export const languages: LanguageConfig[] = [
  {
    lang: 'en',
    i18n: { website: true, app: true, tenantDomain: true },
    programaticPages: { convert: true, compare: true },
    allowAsUserLanguage: true,
  },
  {
    lang: 'de',
    i18n: { website: true, app: true, tenantDomain: true },
    programaticPages: { convert: true, compare: true },
    allowAsUserLanguage: true,
  },
  {
    lang: 'fr',
    i18n: { website: true, app: true, tenantDomain: true },
    programaticPages: { convert: true, compare: true },
    allowAsUserLanguage: true,
  },
  {
    lang: 'es',
    i18n: { website: false, app: false, tenantDomain: false },
    programaticPages: { convert: false, compare: false },
    allowAsUserLanguage: false,
  },
  {
    lang: 'pt',
    i18n: { website: false, app: false, tenantDomain: false },
    programaticPages: { convert: false, compare: false },
    allowAsUserLanguage: false,
  },
  {
    lang: 'it',
    i18n: { website: false, app: false, tenantDomain: false },
    programaticPages: { convert: false, compare: false },
    allowAsUserLanguage: false,
  },
] as const;

export type Language = (typeof languages)[number]['lang'];

/** language tags for SEO and JSON-LD (extracts the keys from languages with website i18n true). */
export const languageTags: Language[] = languages.filter(entry => entry.i18n.website).map(entry => entry.lang);

export const defaultLanguage: Language = 'en';

export const locations = {
  en: ['us'],
  de: ['de'],
  fr: ['fr'],
  es: ['es'],
  pt: ['pt'],
  it: ['it'],
} as const satisfies Record<Language, readonly string[]>;

export const defaultLocations = {
  en: 'us',
  de: 'de',
  fr: 'fr',
  es: 'es',
  pt: 'pt',
  it: 'it',
} as const satisfies Record<Language, string>;

type Locale = {
  [L in keyof typeof locations]:
    L | `${L & string}-${Uppercase<Exclude<(typeof locations)[L][number], (typeof defaultLocations)[L]>>}`;
}[keyof typeof locations];

// Language display labels (native names)
export const localeLabels: Record<Locale, string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  pt: 'Português',
  it: 'Italiano',
};

type LocaleWithoutDefault = Exclude<Locale, typeof defaultLanguage>;

export const localeLoaders: Record<LocaleWithoutDefault, () => Promise<unknown>> = {
  de: () => import('@angular/common/locales/global/de'),
  fr: () => import('@angular/common/locales/global/fr'),
  es: () => import('@angular/common/locales/global/es'),
  pt: () => import('@angular/common/locales/global/pt'),
  it: () => import('@angular/common/locales/global/it'),
};

export const doubleClickTime = 350;

export const biggestGroupTimeDiff = 1000 * 60 * 10;

export const maxAnimatedGifSize = 1024 * 1024 * 3;

export const maxConcurrentUploads = 5;
export const maxConcurrentUploadFileSize = Number(getBytesFromMegabytes(100n));

export const activityPerPage = 50;

export const dragScrollThreshold = 50;

export const assetLoadSettings = {
  galleryScreensToPreloadBefore: 0.5,
  galleryScreensToPreloadAfter: 1,
  galleryScreensToPreGenerate: 5,
  assetPagePreGenerateBefore: 1,
  assetPagePreloadBefore: 1,
  assetPagePreloadAfter: 3,
  assetPagePreGenerateAfter: 10,
  preGenerateHighResFromList: true,
};

export const bigIntDividePresision = 1000000n;

export const defaultDarkBackgroundThreshold = 186;

export const trialValueMultiplier = 0.5;

export const thirdPartyScriptDelay = 1000 * 2;

export const tooltipDelay = 300;

export const assetSideMenuSize = 400;

export const maxWatermarkApplyTime = 1000 * 60 * 5;

export const maxUploadRetryCount = 1;
export const maxPostAssetRetryCount = 1;
export const maxUploadFullRetryCount = 1;

export const maxZipRequestSize = 1024 * 1024 * 1024 * 5;

export const maxResizeBatchRetryCount = 2;
export const assetsPerResizeBatch = 100;

export const referralCodeExpiry = 1000 * 60 * 60 * 24 * 90; // 90 days

export const videoFormats = [
  'mp4',
  'mov',
  'avi',
  'webm',
  'm4v',
  'mkv',
  'asf',
  'hevc',
  'wmv',
  'ogv',
  'f4v',
  'flv',
  '3g2',
  '3gp',
  'mxf',
];

export const rawFormats = [
  'nef',
  'raf',
  'dng',
  'cr2',
  'cr3',
  'nrw',
  'arw',
  'pef',
  'ptx',
  '3fr',
  'fff',
  'orf',
  'iiq',
  'rw2',
  'raw',
  'rwl',
  'dcr',
];

export const specialCaseFormats = ['png', 'webp', 'gif', 'tif', 'tiff', 'svg'];

export const resizedImageDownloadFormatMap = {
  webp: 'webp',
  gif: 'gif',
  tif: 'jpg',
  tiff: 'jpg',
  png: 'png',
};

export const browserRenderableExtensions = ['jpg', 'jpeg', 'gif', 'png', 'webp', 'jfif'];

export const accessLevelOrder = ['no_access', 'view_only', 'review', 'edit'];

export const maxTimeBetweenReloads = 1000 * 60 * 60 * 12;

export const maxFileSize = Number(getBytesFromGigabytes(100n));
export const maxGifSize = Number(getBytesFromMegabytes(50n)); // 50 MB because we don't resize gifs

export const trueUpDurationInDays = 14;

export const msInADay = 1000 * 60 * 60 * 24;

export const debugMaxEntries = 1000;

export const maxAssetsToPreview = 50;

export const longPressDuration = 400;

export const tagColors: TagColorEnumDto[] = [null, 'red', 'orange', 'green', 'teal', 'blue', 'purple', 'pink']; // 'yellow' was removed because it didn't have enough contrast

export const fontSizeMap = {
  small: 1,
  medium: 2,
  large: 3,
  huge: 4,
};

export const GbForStoragePrice = (price: PriceDtoV2) => {
  const value = price.limits.storage_capacity;
  if (!value) return 0;
  const bigInt = BigInt(value);
  return Number(bigInt / 1024n / 1024n / 1024n);
};

export const allFeedbackOptions: GalleryFeedbackOptionsEnum[] = [
  'favourite',
  'comment',
  'status',
  'color_label',
  'annotation',
];

export const feedbackOptionToScopeMapping = {
  favourite: 'asset:favourites',
  color_label: 'asset:color-labels',
  status: 'asset:status',
  comment: 'asset:comments',
  annotation: 'asset:comments',
};

// This is not great.. need to move this to a backend endpoint

export const extraParamsForCreateGalleryFromPreset: CreateGalleryFromScratchDto = {
  title: '',
  public_access: 'review',
  tenant_access: 'edit',
};

export const fallbackCover = {
  overlay_color: 'rgba(31,35,46,0.2)',
  position: '50% 50%',
  size: 'small',
  uuid: '',
  content_position: <[number, number]>[0.5, 0.5],
};

export const standardMemoryCacheExpiry = 120_000;
export const searchMemoryCacheExpiry = 10_000;
export const assumedMaxSearchDelay = 10_000;

export const minTimeBetweenTrialsInDays = 180;

export const allGalleryStatuses: GalleryStatusEnum[] = ['no_status', 'review', 'progress', 'approved'];

export const warningColorPercentage = 15;
export const errorColorPercentage = 5;

// This delay is designed to prevent bots from recording views, and creating visitors
export const BOT_PREVENTION_DELAY = 3000;

export const getRolePermissions = (role: string) => {
  if (!role) return undefined;
  if (role === 'owner') {
    return [
      'tenant-colors:list',
      'tenant-colors:retrieve',
      'multipart:abort',
      'multipart:complete',
      'bulks:retrieve',
      'auth:identification',
      'auth:user',
      'auth:change-password',
      'auth:change-email',
      'tenants:list',
      'profile:retrieve',
      'profile:update',
      'accounts:merge',
      'identities:list',
      'identities:create',
      'identities:delete',
      'identities:relink',
      'watchers:list-own',
      'watchers:delete',
      'galleries:list-shared',
      'assets:upload-user',
      'visitors:create',
      'folders:list-shared',
      'app:bulk',
      'visitors:list',
      'tenants:retrieve',
      'tenants:update',
      'tenants:overview',
      'members:list',
      'members:retrieve',
      'invitations:list',
      'invitations:retrieve',
      'subscriptions:retrieve',
      'customers:retrieve',
      'gallery-presets:list',
      'gallery-presets:create',
      'gallery-presets:retrieve',
      'gallery-presets:update',
      'gallery-presets:delete',
      'gallery-invitations:list',
      'gallery-invitations:create',
      'gallery-invitations:retrieve',
      'gallery-invitations:update',
      'gallery-invitations:delete',
      'domains:list',
      'domains:retrieve',
      'watermarks:list',
      'watermarks:create',
      'watermarks:retrieve',
      'watermarks:update',
      'watermarks:delete',
      'galleries:list',
      'galleries:create',
      'galleries:retrieve',
      'galleries:update',
      'galleries:delete',
      'galleries:retrieve-by-link',
      'galleries:bypass-password',
      'gallery-members:list',
      'gallery-members:create',
      'gallery-members:retrieve',
      'gallery-members:update',
      'gallery-members:delete',
      'gallery-members:delete-any',
      'gallery-viewers:list',
      'gallery-viewers:update',
      'assets:list',
      'assets:create',
      'assets:retrieve',
      'assets:update',
      'assets:delete',
      'assets:like',
      'galleries:update-protected-fields',
      'assets:attach-tag',
      'assets:detach-tag',
      'asset-versions:list',
      'asset-versions:create',
      'asset-versions:retrieve',
      'asset-versions:update',
      'asset-versions:delete',
      'sections:list',
      'sections:create',
      'sections:retrieve',
      'sections:update',
      'sections:delete',
      'tags:list',
      'tags:create',
      'tags:retrieve',
      'tags:update',
      'tags:delete',
      'folders:list',
      'folders:retrieve',
      'folders:create',
      'folders:update',
      'folders:delete',
      'comments:list',
      'comments:create',
      'comments:retrieve',
      'comments:update',
      'comments:delete',
      'comments:like',
      'watchers:list',
      'watchers:create',
      'activities:list',
      'activities:update',
      'gallery-views:create',
      'archives:create',
      'single-asset-downloads:create',
      'user-gallery-favourites:create',
      'user-gallery-favourites:delete',
      'user-folder-favourites:create',
      'user-folder-favourites:delete',
      'groups:list',
      'groups:retrieve',
      'group-users:list',
      'group-galleries:list',
      'folder-invitations:list',
      'folder-invitations:create',
      'folder-invitations:retrieve',
      'folder-invitations:update',
      'folder-invitations:delete',
      'folder-members:list',
      'folder-members:create',
      'folder-members:retrieve',
      'folder-members:update',
      'folder-members:delete',
      'folder-members:delete-any',
      'group-folders:list',
      'ai-image-generations:create',
      'credit:list',
      'credit:overview',
      'tenant-colors:create',
      'tenant-colors:update',
      'tenant-colors:delete',
      'comments:list-private',
      'visitors:delete',
      'asset-versions:access-original',
      'assets:upload-tenant',
      'invitations:create',
      'invitations:update',
      'invitations:delete',
      'domains:create',
      'domains:delete',
      'referrals:list',
      'referrals:delete',
      'subscriptions:create',
      'subscriptions:update',
      'subscriptions:cancel',
      'cards:list',
      'cards:create',
      'cards:delete',
      'invoices:list',
      'customers:update',
      'tenants:update-all-fields',
      'galleries:retrieve-any',
      'galleries:transfer-ownership',
      'folders:retrieve-any',
      'groups:create',
      'groups:update',
      'groups:delete',
      'group-users:add',
      'group-users:remove',
      'group-galleries:detach',
      'group-galleries:update',
      'comments:update-any',
      'comments:delete-any',
      'group-folders:detach',
      'group-folders:update',
    ];
  } else if (role === 'admin') {
    return [
      'tenant-colors:list',
      'tenant-colors:retrieve',
      'multipart:abort',
      'multipart:complete',
      'bulks:retrieve',
      'auth:identification',
      'auth:user',
      'auth:change-password',
      'auth:change-email',
      'tenants:list',
      'profile:retrieve',
      'profile:update',
      'accounts:merge',
      'identities:list',
      'identities:create',
      'identities:delete',
      'identities:relink',
      'watchers:list-own',
      'watchers:delete',
      'galleries:list-shared',
      'assets:upload-user',
      'visitors:create',
      'folders:list-shared',
      'app:bulk',
      'visitors:list',
      'tenants:retrieve',
      'tenants:update',
      'tenants:overview',
      'members:list',
      'members:retrieve',
      'invitations:list',
      'invitations:retrieve',
      'subscriptions:retrieve',
      'customers:retrieve',
      'gallery-presets:list',
      'gallery-presets:create',
      'gallery-presets:retrieve',
      'gallery-presets:update',
      'gallery-presets:delete',
      'gallery-invitations:list',
      'gallery-invitations:create',
      'gallery-invitations:retrieve',
      'gallery-invitations:update',
      'gallery-invitations:delete',
      'domains:list',
      'domains:retrieve',
      'watermarks:list',
      'watermarks:create',
      'watermarks:retrieve',
      'watermarks:update',
      'watermarks:delete',
      'galleries:list',
      'galleries:create',
      'galleries:retrieve',
      'galleries:update',
      'galleries:delete',
      'galleries:retrieve-by-link',
      'galleries:bypass-password',
      'gallery-members:list',
      'gallery-members:create',
      'gallery-members:retrieve',
      'gallery-members:update',
      'gallery-members:delete',
      'gallery-members:delete-any',
      'gallery-viewers:list',
      'gallery-viewers:update',
      'assets:list',
      'assets:create',
      'assets:retrieve',
      'assets:update',
      'assets:delete',
      'assets:like',
      'galleries:update-protected-fields',
      'assets:attach-tag',
      'assets:detach-tag',
      'asset-versions:list',
      'asset-versions:create',
      'asset-versions:retrieve',
      'asset-versions:update',
      'asset-versions:delete',
      'sections:list',
      'sections:create',
      'sections:retrieve',
      'sections:update',
      'sections:delete',
      'tags:list',
      'tags:create',
      'tags:retrieve',
      'tags:update',
      'tags:delete',
      'folders:list',
      'folders:retrieve',
      'folders:create',
      'folders:update',
      'folders:delete',
      'comments:list',
      'comments:create',
      'comments:retrieve',
      'comments:update',
      'comments:delete',
      'comments:like',
      'watchers:list',
      'watchers:create',
      'activities:list',
      'activities:update',
      'gallery-views:create',
      'archives:create',
      'single-asset-downloads:create',
      'user-gallery-favourites:create',
      'user-gallery-favourites:delete',
      'user-folder-favourites:create',
      'user-folder-favourites:delete',
      'groups:list',
      'groups:retrieve',
      'group-users:list',
      'group-galleries:list',
      'folder-invitations:list',
      'folder-invitations:create',
      'folder-invitations:retrieve',
      'folder-invitations:update',
      'folder-invitations:delete',
      'folder-members:list',
      'folder-members:create',
      'folder-members:retrieve',
      'folder-members:update',
      'folder-members:delete',
      'folder-members:delete-any',
      'group-folders:list',
      'ai-image-generations:create',
      'credit:list',
      'credit:overview',
      'tenant-colors:create',
      'tenant-colors:update',
      'tenant-colors:delete',
      'comments:list-private',
      'visitors:delete',
      'asset-versions:access-original',
      'assets:upload-tenant',
      'invitations:create',
      'invitations:update',
      'invitations:delete',
      'domains:create',
      'domains:delete',
      'referrals:list',
      'referrals:delete',
      'subscriptions:create',
      'subscriptions:update',
      'subscriptions:cancel',
      'cards:list',
      'cards:create',
      'cards:delete',
      'invoices:list',
      'customers:update',
      'tenants:update-all-fields',
      'galleries:retrieve-any',
      'galleries:transfer-ownership',
      'folders:retrieve-any',
      'groups:create',
      'groups:update',
      'groups:delete',
      'group-users:add',
      'group-users:remove',
      'group-galleries:detach',
      'group-galleries:update',
      'comments:update-any',
      'comments:delete-any',
      'group-folders:detach',
      'group-folders:update',
    ];
  } else {
    return [
      'tenant-colors:list',
      'tenant-colors:retrieve',
      'multipart:abort',
      'multipart:complete',
      'bulks:retrieve',
      'auth:identification',
      'auth:user',
      'auth:change-password',
      'auth:change-email',
      'tenants:list',
      'profile:retrieve',
      'profile:update',
      'accounts:merge',
      'identities:list',
      'identities:create',
      'identities:delete',
      'identities:relink',
      'watchers:list-own',
      'watchers:delete',
      'galleries:list-shared',
      'assets:upload-user',
      'visitors:create',
      'folders:list-shared',
      'app:bulk',
      'visitors:list',
      'tenants:retrieve',
      'tenants:update',
      'tenants:overview',
      'members:list',
      'members:retrieve',
      'invitations:list',
      'invitations:retrieve',
      'subscriptions:retrieve',
      'customers:retrieve',
      'gallery-presets:list',
      'gallery-presets:create',
      'gallery-presets:retrieve',
      'gallery-presets:update',
      'gallery-presets:delete',
      'gallery-invitations:list',
      'gallery-invitations:create',
      'gallery-invitations:retrieve',
      'gallery-invitations:update',
      'gallery-invitations:delete',
      'domains:list',
      'domains:retrieve',
      'watermarks:list',
      'watermarks:create',
      'watermarks:retrieve',
      'watermarks:update',
      'watermarks:delete',
      'galleries:list',
      'galleries:create',
      'galleries:retrieve',
      'galleries:update',
      'galleries:delete',
      'galleries:retrieve-by-link',
      'galleries:bypass-password',
      'gallery-members:list',
      'gallery-members:create',
      'gallery-members:retrieve',
      'gallery-members:update',
      'gallery-members:delete',
      'gallery-members:delete-any',
      'gallery-viewers:list',
      'gallery-viewers:update',
      'assets:list',
      'assets:create',
      'assets:retrieve',
      'assets:update',
      'assets:delete',
      'assets:like',
      'galleries:update-protected-fields',
      'assets:attach-tag',
      'assets:detach-tag',
      'asset-versions:list',
      'asset-versions:create',
      'asset-versions:retrieve',
      'asset-versions:update',
      'asset-versions:delete',
      'sections:list',
      'sections:create',
      'sections:retrieve',
      'sections:update',
      'sections:delete',
      'tags:list',
      'tags:create',
      'tags:retrieve',
      'tags:update',
      'tags:delete',
      'folders:list',
      'folders:retrieve',
      'folders:create',
      'folders:update',
      'folders:delete',
      'comments:list',
      'comments:create',
      'comments:retrieve',
      'comments:update',
      'comments:delete',
      'comments:like',
      'watchers:list',
      'watchers:create',
      'activities:list',
      'activities:update',
      'gallery-views:create',
      'archives:create',
      'single-asset-downloads:create',
      'user-gallery-favourites:create',
      'user-gallery-favourites:delete',
      'user-folder-favourites:create',
      'user-folder-favourites:delete',
      'groups:list',
      'groups:retrieve',
      'group-users:list',
      'group-galleries:list',
      'folder-invitations:list',
      'folder-invitations:create',
      'folder-invitations:retrieve',
      'folder-invitations:update',
      'folder-invitations:delete',
      'folder-members:list',
      'folder-members:create',
      'folder-members:retrieve',
      'folder-members:update',
      'folder-members:delete',
      'folder-members:delete-any',
      'group-folders:list',
      'ai-image-generations:create',
      'credit:list',
      'credit:overview',
    ];
  }
};

export const currencyMap = {
  AD: 'EUR',
  AE: 'AED',
  AF: 'AFN',
  AG: 'XCD',
  AI: 'XCD',
  AL: 'ALL',
  AM: 'AMD',
  AO: 'AOA',
  AQ: 'EUR',
  AR: 'ARS',
  AS: 'USD',
  AT: 'EUR',
  AU: 'AUD',
  AW: 'AWG',
  AX: 'EUR',
  AZ: 'AZN',
  BA: 'BAM',
  BB: 'BBD',
  BD: 'BDT',
  BE: 'EUR',
  BF: 'XOF',
  BG: 'BGN',
  BH: 'BHD',
  BI: 'BIF',
  BJ: 'XOF',
  BL: 'EUR',
  BM: 'BMD',
  BN: 'BND',
  BO: 'BOB',
  BQ: 'USD',
  BR: 'BRL',
  BS: 'BSD',
  BT: 'BTN',
  BV: 'NOK',
  BW: 'BWP',
  BY: 'BYN',
  BZ: 'BZD',
  CA: 'CAD',
  CC: 'AUD',
  CD: 'CDF',
  CF: 'XAF',
  CG: 'XAF',
  CH: 'CHF',
  CI: 'XOF',
  CK: 'NZD',
  CL: 'CLP',
  CM: 'XAF',
  CN: 'CNY',
  CO: 'COP',
  CR: 'CRC',
  CU: 'CUP',
  CV: 'CVE',
  CW: 'ANG',
  CX: 'AUD',
  CY: 'EUR',
  CZ: 'CZK',
  DE: 'EUR',
  DJ: 'DJF',
  DK: 'DKK',
  DM: 'XCD',
  DO: 'DOP',
  DZ: 'DZD',
  EC: 'USD',
  EE: 'EUR',
  EG: 'EGP',
  EH: 'MAD',
  ER: 'ERN',
  ES: 'EUR',
  ET: 'ETB',
  FI: 'EUR',
  FJ: 'FJD',
  FK: 'FKP',
  FM: 'USD',
  FO: 'DKK',
  FR: 'EUR',
  GA: 'XAF',
  GB: 'GBP',
  GD: 'XCD',
  GE: 'GEL',
  GF: 'EUR',
  GG: 'GBP',
  GH: 'GHS',
  GI: 'GIP',
  GL: 'DKK',
  GM: 'GMD',
  GN: 'GNF',
  GP: 'EUR',
  GQ: 'XAF',
  GR: 'EUR',
  GS: 'GBP',
  GT: 'GTQ',
  GU: 'USD',
  GW: 'XOF',
  GY: 'GYD',
  HK: 'HKD',
  HM: 'AUD',
  HN: 'HNL',
  HR: 'EUR',
  HT: 'HTG',
  HU: 'HUF',
  ID: 'IDR',
  IE: 'EUR',
  IL: 'ILS',
  IM: 'GBP',
  IN: 'INR',
  IO: 'USD',
  IQ: 'IQD',
  IR: 'IRR',
  IS: 'ISK',
  IT: 'EUR',
  JE: 'GBP',
  JM: 'JMD',
  JO: 'JOD',
  JP: 'JPY',
  KE: 'KES',
  KG: 'KGS',
  KH: 'KHR',
  KI: 'AUD',
  KM: 'KMF',
  KN: 'XCD',
  KP: 'KPW',
  KR: 'KRW',
  KW: 'KWD',
  KY: 'KYD',
  KZ: 'KZT',
  LA: 'LAK',
  LB: 'LBP',
  LC: 'XCD',
  LI: 'CHF',
  LK: 'LKR',
  LR: 'LRD',
  LS: 'LSL',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  LY: 'LYD',
  MA: 'MAD',
  MC: 'EUR',
  MD: 'MDL',
  ME: 'EUR',
  MF: 'EUR',
  MG: 'MGA',
  MH: 'USD',
  MK: 'MKD',
  ML: 'XOF',
  MM: 'MMK',
  MN: 'MNT',
  MO: 'MOP',
  MP: 'USD',
  MQ: 'EUR',
  MR: 'MRU',
  MS: 'XCD',
  MT: 'EUR',
  MU: 'MUR',
  MV: 'MVR',
  MW: 'MWK',
  MX: 'MXN',
  MY: 'MYR',
  MZ: 'MZN',
  NA: 'NAD',
  NC: 'XPF',
  NE: 'XOF',
  NF: 'AUD',
  NG: 'NGN',
  NI: 'NIO',
  NL: 'EUR',
  NO: 'NOK',
  NP: 'NPR',
  NR: 'AUD',
  NU: 'NZD',
  NZ: 'NZD',
  OM: 'OMR',
  PA: 'PAB',
  PE: 'PEN',
  PF: 'XPF',
  PG: 'PGK',
  PH: 'PHP',
  PK: 'PKR',
  PL: 'PLN',
  PM: 'EUR',
  PN: 'NZD',
  PR: 'USD',
  PS: 'ILS',
  PT: 'EUR',
  PW: 'USD',
  PY: 'PYG',
  QA: 'QAR',
  RE: 'EUR',
  RO: 'RON',
  RS: 'RSD',
  RU: 'RUB',
  RW: 'RWF',
  SA: 'SAR',
  SB: 'SBD',
  SC: 'SCR',
  SD: 'SDG',
  SE: 'SEK',
  SG: 'SGD',
  SH: 'SHP',
  SI: 'EUR',
  SJ: 'NOK',
  SK: 'EUR',
  SL: 'SLE',
  SM: 'EUR',
  SN: 'XOF',
  SO: 'SOS',
  SR: 'SRD',
  SS: 'SSP',
  ST: 'STN',
  SV: 'SVC',
  SX: 'ANG',
  SY: 'SYP',
  SZ: 'SZL',
  TC: 'USD',
  TD: 'XAF',
  TF: 'EUR',
  TG: 'XOF',
  TH: 'THB',
  TJ: 'TJS',
  TK: 'NZD',
  TL: 'USD',
  TM: 'TMT',
  TN: 'TND',
  TO: 'TOP',
  TR: 'TRY',
  TT: 'TTD',
  TV: 'AUD',
  TW: 'TWD',
  TZ: 'TZS',
  UA: 'UAH',
  UG: 'UGX',
  UM: 'USD',
  US: 'USD',
  UY: 'UYU',
  UZ: 'UZS',
  VA: 'EUR',
  VC: 'XCD',
  VE: 'VED',
  VG: 'USD',
  VI: 'USD',
  VN: 'VND',
  VU: 'VUV',
  WF: 'XPF',
  WS: 'WST',
  YE: 'YER',
  YT: 'EUR',
  ZA: 'ZAR',
  ZM: 'ZMW',
  ZW: 'ZWG',
};

export const mapToStripeLocale = (locale: string): StripeElementLocale => {
  return locale as StripeElementLocale;
};

export const elementsOptions = (origin: string, theme: 'light' | 'dark' = 'light') =>
  <StripeElementsOptionsModeBase>{
    appearance: {
      theme: theme === 'dark' ? ('night' as const) : ('stripe' as const),
      labels: 'floating' as const,
      inputs: 'condensed' as const,
      variables: {
        // Core styling
        borderRadius: '12px',
        fontFamily: 'AkkuratLL, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

        // Colors - Stripe will automatically apply these to tabs, focus states, etc.
        // Values mirror the app theme tokens (see _theme.scss / _colors.scss);
        // Stripe elements can't read CSS variables, so hexes are pinned here.
        ...(theme === 'dark'
          ? {
              colorPrimary: '#5064ff', // color-primary-light (state-primary dark)
              colorBackground: '#181c25', // gray-dark-85 (--app-card-bg dark)
              colorText: '#f1f5f8', // gray-light-20 (--text-strong dark)
              colorDanger: '#fc6767', // palette-red-light (--text-danger dark)
              colorTextSecondary: '#bfcfe1', // gray-medium-40 (--text-muted dark)
              colorTextPlaceholder: '#8595a8', // gray-medium-50 (--text-subtle)
            }
          : {
              colorPrimary: '#4353FF', // Primary brand color (tabs, focus, links)
              colorBackground: '#FFFFFF', // Background color
              colorText: '#1F232E', // text-strong variable
              colorDanger: '#FB4F4F', // text-danger variable
              colorTextSecondary: '#5D6E82', // text-muted variable
              colorTextPlaceholder: '#8595A8', // text-subtle variable
            }),
      },
      rules: {
        // Only custom rules that can't be handled by variables
        '.TermsText': { fontSize: '11px' },
        '.Input': { boxShadow: 'none' },
        '.Tab': { boxShadow: 'none' },
        '.Block': { boxShadow: 'none' },
      },
    },
    fonts: [
      {
        src: 'url("' + origin + '/assets/external/subset-e0b619674400d7387d042996f58c9e54.woff2")',
        family: 'AkkuratLL',
        weight: 'bold',
        style: 'normal',
      },
      {
        src: 'url("' + origin + '/assets/external/subset-2d9d98a25b1128f89314b16b8937e605.woff2")',
        family: 'AkkuratLL',
        weight: 'normal',
        style: 'normal',
      },
    ],
  };

/** Clerk SignIn/SignUp appearance. Hexes mirror app theme tokens — Clerk
 *  can't read CSS variables. Keep in sync with `elementsOptions` / `_theme.scss`. */
export const clerkAppearance = (theme: 'light' | 'dark' = 'light') => ({
  variables: {
    borderRadius: '12px',
    fontFamily: 'AkkuratLL, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ...(theme === 'dark'
      ? {
          colorPrimary: '#5064ff', // state-primary dark
          colorBackground: '#111319', // gray-dark-90 (--layout-base dark)
          colorInputBackground: '#181c25', // gray-dark-85 (--app-card-bg dark)
          colorInputText: '#f1f5f8', // --text-strong dark
          colorText: '#f1f5f8',
          colorTextSecondary: '#bfcfe1', // --text-muted dark
          colorTextOnPrimaryBackground: '#ffffff',
          colorDanger: '#fc6767',
          colorNeutral: '#ffffff',
        }
      : {
          colorPrimary: '#4353FF',
          colorBackground: '#ffffff',
          colorInputBackground: '#ffffff',
          colorInputText: '#1f232e',
          colorText: '#1f232e',
          colorTextSecondary: '#5d6e82',
          colorTextOnPrimaryBackground: '#ffffff',
          colorDanger: '#FB4F4F',
          colorNeutral: '#1f232e',
        }),
  },
  elements: {
    cardBox: { boxShadow: 'none', width: '100%' },
    card: { boxShadow: 'none', border: 'none', background: 'transparent' },
    header: { display: 'none' },
    footer: { display: 'none' },
    logoBox: { display: 'none' },
  },
});

export const paymentElementOptions: StripePaymentElementOptions = {
  layout: {
    type: 'tabs',
  },
  terms: {
    applePay: 'never',
    auBecsDebit: 'never',
    bancontact: 'never',
    card: 'never',
    cashapp: 'never',
    googlePay: 'never',
    ideal: 'never',
    paypal: 'never',
    sepaDebit: 'never',
    sofort: 'never',
    usBankAccount: 'never',
  },
  fields: {
    billingDetails: {
      name: 'auto', // Show if required
      email: 'auto', // Show if required
      phone: 'auto', // Show if required
      address: {
        country: 'auto', // Hide country
        postalCode: 'auto', // Show postal/zip code if needed
        line1: 'auto', // Hide street address
        line2: 'auto', // Hide apartment/suite
        city: 'auto', // Hide city
        state: 'auto', // Hide state/province
      },
    },
  },
};

export const paymentElementOptionsWithoutAddress: StripePaymentElementOptions = {
  ...paymentElementOptions,
  fields: {
    billingDetails: {
      name: 'auto', // Show if required
      email: 'auto', // Show if required
      phone: 'auto', // Show if required
      address: {
        country: 'never', // Hide country
        postalCode: 'never', // Show postal/zip code if needed
        line1: 'never', // Hide street address
        line2: 'never', // Hide apartment/suite
        city: 'never', // Hide city
        state: 'never', // Hide state/province
      },
    },
  },
};
